import React, { ElementType, useEffect, useRef, useState } from 'react';

/**
 * Delays unmounting of component in order to animate state change
 *
 * @param delayTime time to run animation prior to unmounting.
 * @param Component component to delay unmount.
 * @param isVisible boolean controlling mounting of component.
 *
 * ## Usage
 *
 * Wrap component:
 *
  const DelayedComponent = delayUnmountHOC(Component)

  Pass delayTime and isVisible prop to wrapped component:
 *
  <DelayedComponent onDismiss={hideDrawer} delayTime={500} isVisible={isShowingDrawer}>
 *
 To control animation style, access isVisible prop in styled component
 */

interface IDelayUnmountHOC {
  delayTime: number;
  isVisible: boolean;
  [key: string]: any;
}

export const delayUnmountHOC = (Component: ElementType) => {
  return ({ delayTime, isVisible, ...props }: IDelayUnmountHOC) => {
    const [shouldRender, setShouldRender] = useState(isVisible);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      // clear any currently pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (shouldRender && !isVisible) {
        timeoutRef.current = setTimeout(() => setShouldRender(false), delayTime);
      } else if (isVisible && !shouldRender) {
        setShouldRender(true);
      }
    }, [delayTime, shouldRender, isVisible]);

    // also on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return shouldRender ? <Component isVisible={isVisible} {...props} /> : null;
  };
};
